"use client";

import { useEffect, useState } from "react";
import {
  FLOATING_CLEARANCE_SELECTOR,
  FLOATING_SURFACE_BOTTOM_PX,
  measureFloatingClearance,
} from "@/utils/common/floatingClearance";

/** Bottom offset that keeps a floating card above the sticky bar marked with `FLOATING_CLEARANCE_PROPS`. */
export function useFloatingClearance(isActive: boolean): number {
  const [offset, setOffset] = useState(FLOATING_SURFACE_BOTTOM_PX);

  useEffect(() => {
    if (!isActive) return;

    let observed: Element | null = null;
    let frame: number | null = null;

    const measure = () => {
      frame = null;
      // The bar can be swapped (skeleton → editor, step changes), so look it up every time.
      const element = document.querySelector(FLOATING_CLEARANCE_SELECTOR);
      if (element !== observed) {
        if (observed) resizeObserver.unobserve(observed);
        if (element) resizeObserver.observe(element);
        observed = element;
      }
      setOffset(measureFloatingClearance(element, window.innerHeight));
    };
    const scheduleMeasure = () => {
      frame ??= requestAnimationFrame(measure);
    };

    const resizeObserver = new ResizeObserver(scheduleMeasure);
    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", scheduleMeasure);
    measure();

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("resize", scheduleMeasure);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isActive]);

  return offset;
}
